<template>
  <div class="content-wrapper ataviti-home">

    <!-- LOADING -->
    <div
      v-if="loading"
      class="content-body"
    >
      <div class="chantier-state">
        <b-spinner class="mb-1" />
        <p class="mt-1 mb-0">
          Chargement du plan d'action…
        </p>
      </div>
    </div>

    <!-- ERROR -->
    <div
      v-else-if="error"
      class="content-body"
    >
      <div class="chantier-state chantier-state--error">
        <feather-icon
          icon="AlertTriangleIcon"
          size="32"
        />
        <p class="mt-1 mb-1">
          {{ error }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="loadPlan"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Réessayer
        </b-button>
      </div>
    </div>

    <div
      v-else-if="plan && chantier"
      class="content-body"
    >

      <router-link
        :to="{ name: 'chantier-detail', params: { id: chantier.id } }"
        class="chantier-back"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
          class="mr-50"
        />
        Retour au chantier
      </router-link>

      <!-- HERO -->
      <header class="ataviti-home__banner">
        <div class="ataviti-home__banner-text">
          <p class="ataviti-home__banner-eyebrow">
            Plan d'action — {{ chantier.nom }}
          </p>
          <h1 class="ataviti-home__title">
            {{ plan.libelle }}
          </h1>

          <div class="plan-header__meta">
            <div
              v-if="canSeeInternal"
              class="plan-header__responsable"
            >
              <div>
                <div class="plan-header__responsable-label">
                  Responsable
                </div>
                <div class="plan-header__responsable-name">
                  {{ responsableName }}
                </div>
              </div>
            </div>

            <span
              v-if="plan.processusNom"
              class="badge plan-header__processus"
            >
              <feather-icon
                icon="GitBranchIcon"
                size="13"
                class="mr-25"
              />
              Processus&nbsp;: {{ plan.processusNom }}
            </span>
          </div>
        </div>

        <div
          v-if="canEdit"
          class="plan-header__actions"
        >
          <b-button
            variant="outline-primary"
            size="sm"
            @click="openEditModal"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            Modifier
          </b-button>
        </div>
      </header>

      <!-- Statut : 1er élément sous le bandeau, comme sur le détail d'un chantier -->
      <chantier-statut-stepper
        :statuts="statuts"
        :current="plan.statut"
      />

      <!-- Description -->
      <section class="chantier-section">
        <h2 class="chantier-section__title">
          <feather-icon
            icon="FileTextIcon"
            size="18"
            class="mr-50"
          />
          Description
        </h2>
        <p class="chantier-section__text">
          {{ plan.description || 'Aucune description.' }}
        </p>
      </section>

      <!--
        DEFERRED FEATURES (kept in code for a later spec, hidden for the MVP):
        - PDF recap generation
        - date-range progress report
        See plan 2026-05-29-chantiers-front.md (features reportées).
      -->
      <div
        v-if="false"
        class="chantier-actions-panel"
      >
        <div class="chantier-actions-panel__main">
          <feather-icon
            icon="FileTextIcon"
            size="18"
            class="mr-50 chantier-actions-panel__icon"
          />
          <div class="chantier-actions-panel__text">
            <div class="chantier-actions-panel__title">
              Générer un récapitulatif PDF
            </div>
            <div class="chantier-actions-panel__sub">
              Récap complet de ce plan d'action (responsable, liens, réunions, notes).
            </div>
          </div>
          <b-button
            variant="primary"
            size="sm"
            class="chantier-actions-panel__cta"
          >
            <feather-icon
              icon="DownloadIcon"
              size="14"
              class="mr-50"
            />
            Générer le récap
          </b-button>
        </div>
      </div>

      <!-- LIENS -->
      <section class="chantier-section">
        <div class="chantier-section__head">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="LinkIcon"
              size="18"
              class="mr-50"
            />
            Liens & documents
            <span class="chantier-section__count">{{ liens.length + fichiers.length }}</span>
          </h2>
          <div
            v-if="canEdit"
            class="d-flex"
            style="gap: 8px;"
          >
            <b-button
              variant="outline-primary"
              size="sm"
              @click="openLienModal"
            >
              <feather-icon
                icon="LinkIcon"
                size="14"
                class="mr-50"
              />
              Ajouter un lien
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="openFichierModal"
            >
              <feather-icon
                icon="UploadIcon"
                size="14"
                class="mr-50"
              />
              Ajouter un document
            </b-button>
          </div>
        </div>

        <ul
          v-if="liens.length || fichiers.length"
          class="plan-links"
        >
          <li
            v-for="(lien, idx) in liens"
            :key="'lien-' + (lien.id || idx)"
          >
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-50 plan-links__icon"
              aria-hidden="true"
            />
            <b-link
              :href="lien.url"
              target="_blank"
              rel="noopener"
            >
              {{ lien.libelle }}
            </b-link>
            <feather-icon
              icon="ExternalLinkIcon"
              size="12"
              class="ml-25 plan-links__ext"
              aria-hidden="true"
            />
            <b-button
              v-if="canEdit"
              variant="flat-danger"
              size="sm"
              class="ml-50 py-0 px-50"
              :aria-label="'Supprimer le lien ' + lien.libelle"
              @click="confirmDeleteLien(lien, idx)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              />
            </b-button>
          </li>
          <li
            v-for="f in fichiers"
            :key="'doc-' + f.id"
          >
            <feather-icon
              icon="PaperclipIcon"
              size="14"
              class="mr-50 plan-links__icon"
              aria-hidden="true"
            />
            <b-link
              href="#"
              @click.prevent="downloadFichier(f)"
            >
              {{ f.libelle || f.nom }}
            </b-link>
            <feather-icon
              icon="DownloadIcon"
              size="12"
              class="ml-25 plan-links__ext"
              aria-hidden="true"
            />
            <b-button
              v-if="canEdit"
              variant="flat-danger"
              size="sm"
              class="ml-50 py-0 px-50"
              :aria-label="'Supprimer le document ' + (f.libelle || f.nom)"
              @click="confirmDeleteFichier(f)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              />
            </b-button>
          </li>
        </ul>

        <p
          v-else
          class="chantier-section__text plan-empty"
        >
          Aucun lien ni document associé à ce plan d'action.
        </p>
      </section>

      <!-- RÉUNIONS PLANIFIÉES -->
      <section
        v-if="canSeeInternal"
        class="chantier-section"
      >
        <div class="chantier-section__head">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="CalendarIcon"
              size="18"
              class="mr-50"
            />
            Planification des réunions
            <span class="chantier-section__count">{{ reunions.length }}</span>
          </h2>
          <b-button
            v-if="canEdit"
            variant="outline-primary"
            size="sm"
            @click="openReunionsModal"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            Gérer
          </b-button>
        </div>

        <div
          v-if="reunions.length"
          class="plan-meetings"
        >
          <div
            v-for="(r, idx) in sortedMeetings"
            :key="r.id || idx"
            class="plan-meeting"
            :class="{ 'plan-meeting--past': isPast(r.dateHeure) }"
          >
            <div class="plan-meeting__date">
              <div class="plan-meeting__day">
                {{ formatDay(r.dateHeure) }}
              </div>
              <div class="plan-meeting__month">
                {{ formatMonth(r.dateHeure) }}
              </div>
              <div class="plan-meeting__year">
                {{ formatYear(r.dateHeure) }}
              </div>
            </div>
            <div class="plan-meeting__body">
              <div class="plan-meeting__title">
                {{ r.libelle }}
              </div>
              <div class="plan-meeting__time">
                <span
                  v-if="isPast(r.dateHeure)"
                  class="badge badge-light plan-meeting__status"
                >Passée</span>
                <span
                  v-else
                  class="badge plan-meeting__status plan-meeting__status--upcoming"
                >
                  À venir
                </span>
              </div>
              <div
                v-if="(r.invites || []).length"
                class="plan-meeting__invites"
              >
                <feather-icon
                  icon="UsersIcon"
                  size="12"
                  class="mr-25"
                />
                {{ inviteNames(r.invites) }}
              </div>
            </div>
          </div>
        </div>
        <p
          v-else
          class="chantier-section__text plan-empty"
        >
          Aucune réunion planifiée.
        </p>
      </section>

      <!-- NOTES DE RÉUNION -->
      <section class="chantier-section">
        <div class="chantier-section__head">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="MessageSquareIcon"
              size="18"
              class="mr-50"
            />
            Notes de réunion
            <span class="chantier-section__count">{{ notes.length }}</span>
          </h2>
          <b-button
            v-if="canEdit"
            variant="primary"
            size="sm"
            @click="goToNewNote"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Nouvelle note
          </b-button>
        </div>

        <div
          v-if="notesLoading"
          class="text-center py-2"
        >
          <b-spinner small />
        </div>
        <div
          v-else-if="notes.length"
          class="plan-notes"
        >
          <article
            v-for="note in sortedNotes"
            :key="note.id"
            class="plan-note"
            @click="goToNote(note)"
          >
            <div class="plan-note__head">
              <div class="plan-note__date">
                {{ formatLongDate(note.date) }}
              </div>
              <span
                v-if="chantier && chantier.visibilite === 1 && note.aRedigeCompteRenduPublic"
                class="badge plan-note__public"
              >
                <feather-icon
                  icon="GlobeIcon"
                  size="11"
                  class="mr-25"
                />
                Compte-rendu public disponible
              </span>
              <span
                v-else
                class="badge plan-note__internal"
              >
                <feather-icon
                  icon="LockIcon"
                  size="11"
                  class="mr-25"
                />
                Interne uniquement
              </span>
            </div>
            <h3 class="plan-note__title">
              {{ note.titre }}
            </h3>
            <div
              v-if="note.aRedactionInterne"
              class="plan-note__internal-banner"
            >
              <feather-icon
                icon="EyeOffIcon"
                size="11"
                class="mr-25"
              />
              Contient des notes internes (visibles uniquement par les participants)
            </div>
          </article>
        </div>
        <p
          v-else
          class="chantier-section__text plan-empty"
        >
          Aucune note pour le moment.
        </p>
      </section>

      <!-- ÉDITION DU PLAN -->
      <b-modal
        id="plan-edit-modal"
        title="Modifier le plan d'action"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        :ok-disabled="saving"
        centered
        size="lg"
        @ok.prevent="submitEdit"
        :no-close-on-backdrop="true"
      >
        <b-form-group
          label="Libellé"
          label-for="plan-edit-libelle"
        >
          <b-form-input
            id="plan-edit-libelle"
            v-model="editModel.libelle"
          />
        </b-form-group>
        <b-form-group
          label="Description"
          label-for="plan-edit-desc"
        >
          <b-form-textarea
            id="plan-edit-desc"
            v-model="editModel.description"
            rows="3"
          />
        </b-form-group>
        <b-form-group label="Responsable">
          <searchable-vue-select
            v-model="editResponsable"
            placeholder="Rechercher un utilisateur"
            :options="listUtilisateurs"
            label="nomComplet"
            :has-next-page="listUtilisateurs.length < utilisateursTotalCount"
            @option:clear="searchUtilisateurs()"
            @load-next-page="(params) => searchUtilisateurs(params)"
          />
        </b-form-group>
        <div class="row">
          <div class="col-12 col-md-6">
            <b-form-group
              label="Statut"
              label-for="plan-edit-statut"
            >
              <b-form-select
                id="plan-edit-statut"
                v-model="editModel.statut"
                :options="statutOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Échéance"
              label-for="plan-edit-echeance"
            >
              <b-form-input
                id="plan-edit-echeance"
                v-model="editModel.dateEcheance"
                type="date"
              />
            </b-form-group>
          </div>
        </div>
        <b-form-group label="Processus">
          <searchable-vue-select
            v-model="editProcessus"
            :options="processusOptions"
            label="libelle"
            :has-next-page="false"
            :load="false"
            placeholder="Processus (optionnel)"
          />
        </b-form-group>
        <p
          v-if="modalError"
          class="text-danger mb-0"
        >
          {{ modalError }}
        </p>
      </b-modal>

      <!-- LIEN MODAL -->
      <b-modal
        id="plan-lien-modal"
        title="Ajouter un lien"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        :ok-disabled="saving"
        centered
        @ok.prevent="submitLien"
      >
        <b-form-group
          label="Libellé"
          label-for="plan-lien-libelle"
        >
          <b-form-input
            id="plan-lien-libelle"
            v-model="lienForm.libelle"
          />
        </b-form-group>
        <b-form-group
          label="URL"
          label-for="plan-lien-url"
        >
          <b-form-input
            id="plan-lien-url"
            v-model="lienForm.url"
            type="url"
            placeholder="https://…"
          />
        </b-form-group>
        <p
          v-if="modalError"
          class="text-danger mb-0"
        >
          {{ modalError }}
        </p>
      </b-modal>

      <!-- DOCUMENT MODAL -->
      <b-modal
        id="plan-fichier-modal"
        title="Ajouter un document"
        ok-title="Téléverser"
        cancel-title="Annuler"
        :ok-disabled="uploadingFichier || !selectedFichier"
        :busy="uploadingFichier"
        centered
        @ok.prevent="submitFichier"
      >
        <b-form-group
          label="Fichier"
          label-for="plan-fichier-input"
        >
          <b-form-file
            id="plan-fichier-input"
            v-model="selectedFichier"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            browse-text="Parcourir"
            placeholder="Cliquez ici ou glissez-y votre fichier"
            drop-placeholder="Vous pouvez lâcher votre fichier"
          />
          <small class="text-muted">PDF ou Office (Word/Excel/PowerPoint), 50 Mo max.</small>
        </b-form-group>
        <b-form-group
          label="Libellé (optionnel)"
          label-for="plan-fichier-libelle"
        >
          <b-form-input
            id="plan-fichier-libelle"
            v-model="fichierLibelle"
            placeholder="Nom affiché (défaut : nom du fichier)"
          />
        </b-form-group>
        <p
          v-if="fichierError"
          class="text-danger mb-0"
        >
          {{ fichierError }}
        </p>
      </b-modal>

      <!-- RÉUNIONS MODAL -->
      <b-modal
        id="plan-reunions-modal"
        title="Gérer les réunions"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        :ok-disabled="saving"
        centered
        size="lg"
        @ok.prevent="submitReunions"
        :no-close-on-backdrop="true"
      >
        <div
          v-for="(reunion, idx) in editReunions"
          :key="idx"
          class="plan-edit-block"
        >
          <div class="d-flex justify-content-between align-items-center mb-50">
            <strong>Réunion {{ idx + 1 }}</strong>
            <b-button
              variant="flat-danger"
              size="sm"
              @click="removeReunion(idx)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
              />
            </b-button>
          </div>
          <b-form-input
            v-model="reunion.libelle"
            placeholder="Libellé"
            class="mb-1"
          />
          <b-form-input
            v-model="reunion.dateHeure"
            type="date"
            class="mb-1"
          />
          <b-form-group
            label="Invités"
            class="mb-0"
          >
            <searchable-vue-select
              v-model="reunion.invitesSelected"
              placeholder="Rechercher des utilisateurs"
              :options="listUtilisateurs"
              label="nomComplet"
              multiple
              :has-next-page="listUtilisateurs.length < utilisateursTotalCount"
              @option:clear="searchUtilisateurs()"
              @load-next-page="(params) => searchUtilisateurs(params)"
            />
          </b-form-group>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="addReunion"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-50"
          />
          Ajouter une réunion
        </b-button>
        <p
          v-if="modalError"
          class="text-danger mb-0 mt-1"
        >
          {{ modalError }}
        </p>
      </b-modal>

    </div>

    <div
      v-else
      class="content-body"
    >
      <div class="chantier-warning">
        <feather-icon
          icon="AlertTriangleIcon"
          size="16"
          class="mr-50"
        />
        Plan d'action introuvable.
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BLink,
  BModal,
  BSpinner,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormFile,
  BFormSelect,
} from 'bootstrap-vue'
import SearchableVueSelect from '@/components/selects/SearchableVueSelect.vue'
import ChantierStatutStepper from '@/components/chantiers/ChantierStatutStepper.vue'
import { toDateInput, todayDateInput } from './date-input'

const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
const MAX_FICHIER_SIZE = 50 * 1024 * 1024 // 50 Mo

const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
const MONTHS_LONG = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

export default {
  name: 'PlanActionDetailView',
  components: {
    BButton,
    BLink,
    BModal,
    BSpinner,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormFile,
    BFormSelect,
    SearchableVueSelect,
    ChantierStatutStepper,
  },
  data() {
    return {
      chantier: null,
      plan: null,
      statuts: [],
      notes: [],
      loading: true,
      notesLoading: false,
      error: null,
      saving: false,
      modalError: null,
      listUtilisateurs: [],
      utilisateursTotalCount: 0,
      editModel: {
        libelle: '', description: '', statut: null, dateEcheance: '',
      },
      editResponsable: null,
      editProcessus: null,
      processusOptions: [],
      lienForm: { libelle: '', url: '' },
      editReunions: [],
      selectedFichier: null,
      fichierLibelle: '',
      fichierError: null,
      uploadingFichier: false,
    }
  },
  computed: {
    statutOptions() {
      return this.statuts.map(s => ({ value: s.id, text: s.valeur }))
    },
    canSeeInternal() {
      return !!(this.chantier && this.chantier.userCanSeeInternal)
    },
    isArchived() {
      // Chantier archivé = lecture seule totale (aligné sur ChantierDetailView + les gardes serveur).
      return !!(this.chantier && this.chantier.archivedAt)
    },
    canEdit() {
      return !!(this.chantier && this.chantier.userCanManage
        && this.chantier.userCanSeeInternal && this.$can('CHANTIER:update'))
        && !this.isArchived
    },
    liens() {
      return (this.plan && this.plan.liens) || []
    },
    fichiers() {
      return (this.plan && this.plan.fichiers) || []
    },
    reunions() {
      return (this.plan && this.plan.reunionsPlanifiees) || []
    },
    responsableName() {
      const r = this.plan && this.plan.responsable
      if (!r) return '—'
      return r.nomComplet || `${r.prenom || ''} ${r.nom || ''}`.trim() || '—'
    },
    sortedMeetings() {
      return [...this.reunions].sort(
        (a, b) => toDateInput(a.dateHeure).localeCompare(toDateInput(b.dateHeure)),
      )
    },
    sortedNotes() {
      return [...this.notes].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      )
    },
  },
  created() {
    this.loadPlan()
  },
  methods: {
    async loadPlan() {
      this.loading = true
      this.error = null
      const { chantierId, planId } = this.$route.params
      try {
        const [chantier, statuts] = await Promise.all([
          this.$http.myressif.chantiers.getById(chantierId),
          this.$http.myressif.chantiers.getStatuts(),
        ])
        this.chantier = chantier
        this.statuts = statuts || []
        this.plan = (chantier.plansAction || []).find(p => p.id === planId) || null
        if (this.plan) {
          await this.loadNotes()
        }
      } catch (e) {
        this.chantier = null
        this.plan = null
        this.error = "Une erreur est survenue lors du chargement du plan d'action."
      } finally {
        this.loading = false
      }
    },
    async loadNotes() {
      this.notesLoading = true
      const { chantierId, planId } = this.$route.params
      try {
        const response = await this.$http.myressif.chantiers.listNotes(chantierId, planId)
        this.notes = (response && response.items) || []
      } catch (e) {
        this.notes = []
      } finally {
        this.notesLoading = false
      }
    },
    async searchUtilisateurs(params) {
      // Liste paginée des utilisateurs : reset (ouverture/recherche/clear) → page 1 ;
      // scroll (reset=false, pageNumber incrémenté) → on concatène les pages suivantes.
      const search = (params && params.search) || ''
      const reset = !params || params.reset
      const pageNumber = reset ? 1 : ((params && params.pageNumber) || 1)
      try {
        const response = await this.$http.myressif.utilisateurs
          .paginatedList(pageNumber, 10, search, false, 'nom', false)
        const items = (response && response.items) || []
        this.listUtilisateurs = reset
          ? items
          : this.listUtilisateurs.concat(items.filter(ri => !this.listUtilisateurs.some(e => e.id === ri.id)))
        this.utilisateursTotalCount = (response && response.totalCount) || this.listUtilisateurs.length
      } catch (e) {
        if (reset) { this.listUtilisateurs = []; this.utilisateursTotalCount = 0 }
      }
      return this.listUtilisateurs
    },
    // Build the part of the UpdatePlanActionModel that stays unchanged between
    // the responsable/liens/reunions modals so each submit preserves the rest.
    basePlanModel() {
      return {
        libelle: this.plan.libelle,
        description: this.plan.description || undefined,
        responsableId: this.plan.responsable ? this.plan.responsable.id : undefined,
        processusId: this.plan.processusId || undefined,
        dateEcheance: this.plan.dateEcheance || undefined,
        statut: this.plan.statut,
        liens: (this.plan.liens || []).map(l => ({ id: l.id, libelle: l.libelle, url: l.url })),
        reunions: (this.plan.reunionsPlanifiees || []).map(r => ({
          id: r.id,
          libelle: r.libelle,
          dateHeure: r.dateHeure,
          inviteIds: (r.invites || []).map(i => i.id),
        })),
      }
    },
    async savePlan(model) {
      const { chantierId, planId } = this.$route.params
      await this.$http.myressif.chantiers.updatePlan(chantierId, planId, model)
      await this.loadPlan()
    },
    // ---- Documents (fichiers) ----
    openFichierModal() {
      this.selectedFichier = null
      this.fichierLibelle = ''
      this.fichierError = null
      this.$bvModal.show('plan-fichier-modal')
    },
    async submitFichier() {
      const file = this.selectedFichier
      if (!file) return
      const ext = (file.name.split('.').pop() || '').toLowerCase()
      if (!ALLOWED_EXT.includes(ext)) {
        this.fichierError = 'Type de fichier non autorisé (PDF ou Office uniquement).'
        return
      }
      if (file.size > MAX_FICHIER_SIZE) {
        this.fichierError = 'Le fichier dépasse la taille maximale de 50 Mo.'
        return
      }
      this.uploadingFichier = true
      this.fichierError = null
      try {
        const { chantierId, planId } = this.$route.params
        await this.$http.myressif.chantiers.addPlanFichier(chantierId, planId, file, this.fichierLibelle || undefined)
        this.$bvModal.hide('plan-fichier-modal')
        await this.loadPlan()
      } catch (e) {
        this.fichierError = "L'envoi du document a échoué."
      } finally {
        this.uploadingFichier = false
      }
    },
    async downloadFichier(fichier) {
      try {
        const { chantierId, planId } = this.$route.params
        const url = await this.$http.myressif.chantiers.downloadPlanFichier(chantierId, planId, fichier.id)
        if (url) window.open(url)
      } catch (e) {
        // silencieux : l'utilisateur peut réessayer
      }
    },
    confirmDeleteFichier(fichier) {
      this.$bvModal
        .msgBoxConfirm(`Supprimer le document « ${fichier.libelle || fichier.nom} » ?`, {
          title: 'Confirmation de suppression',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => { if (value === true) this.doDeleteFichier(fichier) })
    },
    async doDeleteFichier(fichier) {
      try {
        const { chantierId, planId } = this.$route.params
        await this.$http.myressif.chantiers.deletePlanFichier(chantierId, planId, fichier.id)
        await this.loadPlan()
      } catch (e) {
        // silencieux
      }
    },
    // ---- Édition (infos du plan) ----
    async searchProcessus() {
      try {
        const response = await this.$http.myressif.chantiers.listProcessus()
        this.processusOptions = (response && response.items) || []
      } catch (e) {
        this.processusOptions = []
      }
    },
    formatDateInput(iso) {
      // Champ date-seule : date calendaire telle que sérialisée par le backend (DateTimeOffset),
      // sans conversion de fuseau -> pas de décalage ±1 jour. Helper partagé avec ChantierDetailView.
      return toDateInput(iso)
    },
    async openEditModal() {
      this.modalError = null
      await Promise.all([this.searchUtilisateurs(), this.searchProcessus()])
      this.editModel = {
        libelle: this.plan.libelle || '',
        description: this.plan.description || '',
        statut: this.plan.statut,
        dateEcheance: this.formatDateInput(this.plan.dateEcheance),
      }
      const r = this.plan.responsable
      this.editResponsable = r
        ? { id: r.id, nomComplet: this.responsableName }
        : null
      this.editProcessus = this.plan.processusId
        ? { id: this.plan.processusId, libelle: this.plan.processusNom }
        : null
      this.$bvModal.show('plan-edit-modal')
    },
    async submitEdit() {
      if (!this.editModel.libelle || !this.editModel.libelle.trim()) {
        this.modalError = 'Le libellé est obligatoire.'
        return
      }
      if (!this.editResponsable) {
        this.modalError = 'Le responsable est obligatoire.'
        return
      }
      this.saving = true
      this.modalError = null
      try {
        const model = this.basePlanModel()
        model.libelle = this.editModel.libelle.trim()
        model.description = this.editModel.description || undefined
        model.responsableId = this.editResponsable.id
        model.statut = this.editModel.statut
        model.processusId = this.editProcessus ? this.editProcessus.id : undefined
        model.dateEcheance = this.editModel.dateEcheance || undefined
        await this.savePlan(model)
        this.$bvModal.hide('plan-edit-modal')
      } catch (e) {
        this.modalError = 'La mise à jour a échoué.'
      } finally {
        this.saving = false
      }
    },
    // ---- Liens ----
    openLienModal() {
      this.lienForm = { libelle: '', url: '' }
      this.modalError = null
      this.$bvModal.show('plan-lien-modal')
    },
    async submitLien() {
      const libelle = (this.lienForm.libelle || '').trim()
      let url = (this.lienForm.url || '').trim()
      if (!libelle || !url) {
        this.modalError = 'Le libellé et l\'URL sont obligatoires.'
        return
      }
      // Préfixe le schéma si absent, sinon le :href serait traité comme une route SPA.
      if (!/^https?:\/\//i.test(url)) url = `https://${url}`
      this.saving = true
      this.modalError = null
      try {
        const model = this.basePlanModel()
        model.liens.push({ libelle, url })
        await this.savePlan(model)
        this.$bvModal.hide('plan-lien-modal')
      } catch (e) {
        this.modalError = 'La création du lien a échoué.'
      } finally {
        this.saving = false
      }
    },
    confirmDeleteLien(lien, idx) {
      this.$bvModal
        .msgBoxConfirm(`Supprimer le lien « ${lien.libelle} » ?`, {
          title: 'Confirmation de suppression',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => { if (value === true) this.doDeleteLien(idx) })
    },
    async doDeleteLien(idx) {
      try {
        // Suppression par index : basePlanModel() preserve l'ordre de this.plan.liens
        // (= la liste affichée), robuste même si un lien n'a pas d'id.
        const model = this.basePlanModel()
        model.liens.splice(idx, 1)
        await this.savePlan(model)
      } catch (e) {
        // silencieux
      }
    },
    // ---- Réunions ----
    async openReunionsModal() {
      this.modalError = null
      await this.searchUtilisateurs()
      this.editReunions = (this.plan.reunionsPlanifiees || []).map(r => ({
        id: r.id,
        libelle: r.libelle,
        dateHeure: toDateInput(r.dateHeure),
        invitesSelected: (r.invites || []).map(i => ({
          id: i.id,
          nomComplet: i.nomComplet || `${i.prenom || ''} ${i.nom || ''}`.trim() || i.id,
        })),
      }))
      this.$bvModal.show('plan-reunions-modal')
    },
    addReunion() {
      this.editReunions.push({ libelle: '', dateHeure: '', invitesSelected: [] })
    },
    removeReunion(idx) {
      this.editReunions.splice(idx, 1)
    },
    async submitReunions() {
      this.saving = true
      this.modalError = null
      try {
        const model = this.basePlanModel()
        model.reunions = this.editReunions
          .filter(r => r.libelle || r.dateHeure)
          .map(r => {
            const out = {
              libelle: r.libelle,
              dateHeure: r.dateHeure, // jour seul 'YYYY-MM-DD', surtout pas de toISOString() (décalage ±1 jour)
              inviteIds: (r.invitesSelected || []).map(i => i.id),
            }
            if (r.id) out.id = r.id
            return out
          })
        await this.savePlan(model)
        this.$bvModal.hide('plan-reunions-modal')
      } catch (e) {
        this.modalError = 'La mise à jour des réunions a échoué.'
      } finally {
        this.saving = false
      }
    },
    inviteNames(invites) {
      return (invites || [])
        .map(i => i.nomComplet || `${i.prenom || ''} ${i.nom || ''}`.trim() || i.id)
        .join(', ')
    },
    // Réunions = jour seul : on lit 'YYYY-MM-DD' dans la chaîne (insensible au fuseau). Une réunion du jour reste « À venir ».
    isPast(iso) { return toDateInput(iso) < todayDateInput() },
    formatDay(iso) { return toDateInput(iso).slice(8, 10) },
    formatMonth(iso) { return MONTHS_SHORT[Number(toDateInput(iso).slice(5, 7)) - 1] },
    formatYear(iso) { return toDateInput(iso).slice(0, 4) },
    formatLongDate(iso) {
      const d = new Date(iso)
      return `${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`
    },
    goToNewNote() {
      this.$router.push({
        name: 'chantier-plan-note-new',
        params: { chantierId: this.chantier.id, planId: this.plan.id },
      })
    },
    goToNote(note) {
      this.$router.push({
        name: 'chantier-plan-note-detail',
        params: { chantierId: this.chantier.id, planId: this.plan.id, noteId: note.id },
      })
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-yellow:  #fcf5a6;
$at-orange:  #ff6131;
$at-beige:   #fffced;
$at-ink-900: #0F1F1F;
$at-ink-700: #2A3F3F;
$at-ink-500: #5A7373;
$at-ink-300: #A6B8B8;
$at-ink-100: #E4ECEC;

.chantier-back {
  display: inline-flex;
  align-items: center;
  color: $at-ink-500;
  font-size: 13px;
  margin-bottom: 12px;
  text-decoration: none;
  &:hover { color: $at-orange; }
}

.chantier-state {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}

// Hero
.ataviti-home {
  &__banner {
    background: $at-yellow;
    border-radius: 12px;
    padding: 28px 32px;
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  &__banner-eyebrow {
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin: 0 0 8px;
  }
  &__title {
    font-family: 'Iowan Old Style', 'Palatino Linotype', Georgia, serif;
    font-size: 26px;
    color: $at-green;
    font-weight: 500;
    margin: 0 0 16px;
    letter-spacing: -.01em;
    line-height: 1.2;
  }
}

.plan-header {
  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }
  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  &__responsable {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, .55);
    border-radius: 999px;
    padding: 8px 16px;
  }
  &__responsable-label {
    font-size: 10px;
    color: $at-ink-500;
    text-transform: uppercase;
    letter-spacing: .04em;
  }
  &__responsable-name {
    font-size: 13px;
    color: $at-ink-900;
    font-weight: 600;
  }
  &__processus {
    background: rgba(255, 97, 49, .15);
    color: $at-orange;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
  }
}

.chantier-section {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 18px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;

    .chantier-section__title { margin-bottom: 0; }
  }

  &__title {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: $at-ink-900;
    font-weight: 600;
    margin: 0 0 14px;
  }
  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(28, 77, 77, .10);
    color: $at-green;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    margin-left: 8px;
  }
  &__text { color: $at-ink-700; font-size: 14px; line-height: 1.6; margin: 0; }
}

.plan-empty {
  color: $at-ink-500;
  font-style: italic;
}

.plan-edit-block {
  border: 1px solid $at-ink-100;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.chantier-warning {
  display: flex;
  align-items: center;
  background: rgba(240, 168, 48, .12);
  border: 1px solid rgba(240, 168, 48, .35);
  color: #8a5a0a;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 18px;
  font-size: 13px;
}

.chantier-actions-panel {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 18px;

  &__main {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  &__icon { color: $at-green; flex-shrink: 0; }
  &__text { flex: 1; min-width: 220px; }
  &__title {
    font-size: 14px;
    font-weight: 600;
    color: $at-ink-900;
  }
  &__sub {
    font-size: 12px;
    color: $at-ink-500;
    margin-top: 2px;
  }
  &__cta { font-weight: 500; }
}

// Liens — card-like rows with hover
.plan-links {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid transparent;
    font-size: 14px;
    transition: background-color 140ms ease, border-color 140ms ease, transform 120ms ease;
    cursor: pointer;
    margin-bottom: 4px;

    &:hover {
      background: $at-beige;
      border-color: $at-ink-100;
      transform: translateX(2px);

      a { color: $at-green; text-decoration: underline; }
      .plan-links__icon { color: $at-orange; }
    }

    a {
      color: $at-orange;
      font-weight: 500;
      flex: 1;
    }
  }
  &__icon { color: $at-ink-500; transition: color 140ms ease; }
  &__ext { color: $at-ink-300; }
}

// Meetings
.plan-meetings {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.plan-meeting {
  display: flex;
  align-items: stretch;
  background: $at-beige;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  overflow: hidden;

  &--past {
    opacity: .80;
    background: #fff;
  }

  &__date {
    background: rgba(28, 77, 77, .08);
    color: $at-green;
    padding: 10px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 80px;
  }
  &__day {
    font-size: 22px;
    font-weight: 700;
    line-height: 1;
  }
  &__month {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-top: 2px;
  }
  &__year {
    font-size: 10px;
    color: $at-ink-500;
    margin-top: 2px;
  }
  &__body {
    padding: 12px 16px;
    flex: 1;
  }
  &__title {
    font-size: 14px;
    font-weight: 600;
    color: $at-ink-900;
    margin-bottom: 4px;
  }
  &__time {
    font-size: 12px;
    color: $at-ink-500;
    display: inline-flex;
    align-items: center;
  }
  &__invites {
    font-size: 12px;
    color: $at-ink-500;
    display: inline-flex;
    align-items: center;
    margin-top: 6px;
  }
  &__status {
    margin-left: 10px;
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: .04em;

    &--upcoming {
      background: rgba(47, 125, 107, .15);
      color: #2f7d6b;
    }
  }
}

// Notes
.plan-notes {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.plan-note {
  background: $at-beige;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 16px 18px;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &:hover {
    border-color: $at-green;
    box-shadow: 0 4px 14px rgba(28, 77, 77, .08);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    flex-wrap: wrap;
    gap: 8px;
  }
  &__date {
    font-size: 12px;
    color: $at-ink-500;
    text-transform: uppercase;
    letter-spacing: .04em;
    font-weight: 600;
  }
  &__public {
    background: rgba(47, 125, 107, .15);
    color: #2f7d6b;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 9px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
  }
  &__internal {
    background: rgba(28, 77, 77, .10);
    color: $at-green;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 9px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
  }
  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $at-ink-900;
    margin: 0 0 6px;
  }
  &__internal-banner {
    background: rgba(255, 97, 49, .08);
    color: $at-orange;
    border-left: 3px solid $at-orange;
    font-size: 11px;
    font-weight: 500;
    padding: 6px 10px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
  }
}
</style>
